export class PlatformApiError extends Error {
    status;
    code;
    constructor(status, code) {
        super(`CREST API request failed with ${status}: ${code}`);
        this.status = status;
        this.code = code;
        this.name = 'PlatformApiError';
    }
}
export class PlatformApiAuthenticationError extends Error {
    constructor() {
        super('CREST API authentication is required.');
        this.name = 'PlatformApiAuthenticationError';
    }
}
const platformEntityTagPattern = /^(?:W\/)?"sha256:[a-f0-9]{64}"$/;
function responseEntityTag(value) {
    return value !== null && platformEntityTagPattern.test(value) ? value : null;
}
function errorCode(body) {
    return typeof body === 'object' && body !== null && 'error' in body
        ? String(body.error?.code ?? 'request_failed')
        : 'request_failed';
}
function isSameOriginApiTarget(target) {
    try {
        const currentOrigin = typeof window === 'undefined' ? 'https://crest.local' : window.location.origin;
        return new URL(target, currentOrigin).origin === currentOrigin;
    }
    catch {
        return false;
    }
}
export class PlatformApiClient {
    baseUrl;
    authentication;
    constructor(options = '') {
        if (typeof options === 'string') {
            this.baseUrl = options;
            this.authentication = undefined;
            return;
        }
        this.baseUrl = options.baseUrl ?? '';
        this.authentication = options.authentication;
    }
    async request(path, schema, init = {}) {
        const result = await this.requestConditional(path, schema, { init });
        if (result.status === 'not-modified') {
            throw new PlatformApiError(304, 'not_modified_without_cached_representation');
        }
        return result.value;
    }
    async requestConditional(path, schema, options = {}) {
        const init = options.init ?? {};
        const headers = new Headers(init.headers);
        if (!headers.has('accept'))
            headers.set('accept', 'application/json');
        if (init.body !== undefined && !headers.has('content-type'))
            headers.set('content-type', 'application/json');
        if (options.entityTag !== undefined)
            headers.set('if-none-match', options.entityTag);
        const target = `${this.baseUrl}${path}`;
        const sameOrigin = isSameOriginApiTarget(target);
        if (this.authentication !== undefined && sameOrigin) {
            const accessToken = await this.authentication.getAccessToken();
            if (accessToken === null || accessToken === '')
                throw new PlatformApiAuthenticationError();
            headers.set('authorization', `Bearer ${accessToken}`);
            headers.set('x-crest-tenant-id', this.authentication.tenantId);
        }
        const response = await fetch(target, {
            ...init,
            credentials: sameOrigin ? 'include' : 'omit',
            headers,
        });
        if (response.status === 304) {
            const entityTag = responseEntityTag(response.headers.get('etag')) ?? options.entityTag;
            if (entityTag === undefined) {
                throw new PlatformApiError(304, 'not_modified_without_entity_tag');
            }
            return { status: 'not-modified', entityTag };
        }
        const body = await response.json().catch(() => null);
        if (!response.ok) {
            throw new PlatformApiError(response.status, errorCode(body));
        }
        return {
            status: 'modified',
            value: schema.parse(body),
            entityTag: responseEntityTag(response.headers.get('etag')),
        };
    }
}
//# sourceMappingURL=platform-api-client.js.map